import React from "react";
import {
  TruckOutlined,
  ClockCircleOutlined,
  CarOutlined,
  WarningOutlined,
  SendOutlined,
} from "@ant-design/icons";
import styled from "styled-components";

const CardsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
  position: relative;
  z-index: 1;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const CompactRow = styled.div`
  display: none;
  margin-bottom: 12px;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const Chip = styled.button`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  min-width: 88px;
  padding: 10px 12px;
  border-radius: 14px;
  border: 1.5px solid ${(props) => (props.$active ? props.$accent : "#e5e7eb")};
  background: ${(props) => (props.$active ? props.$bg : "#ffffff")};
  color: ${(props) => (props.$active ? props.$accent : "#374151")};
  cursor: pointer;
  box-shadow: ${(props) =>
    props.$emphasis && !props.$active
      ? "0 0 0 1px rgba(255, 77, 79, 0.2)"
      : "none"};
`;

const ChipTop = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: ${(props) => props.$bg};
  color: ${(props) => props.$color};
  font-size: 14px;
`;

const ChipCount = styled.span`
  font-size: 20px;
  font-weight: 800;
  line-height: 1;
  color: ${(props) => props.$color || "#111827"};
`;

const ChipLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
`;

const Card = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid ${(props) => (props.$active ? props.$accent : "#f0f0f0")};
  background: ${(props) => (props.$active ? props.$bg : "#fff")};
  box-shadow: ${(props) =>
    props.$emphasis && !props.$active
      ? "0 0 0 1px rgba(255, 77, 79, 0.25)"
      : "none"};
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: ${(props) => props.$accent};
  }
`;

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${(props) => props.$bg};
  color: ${(props) => props.$color};
  font-size: 18px;
  flex-shrink: 0;
`;

const Meta = styled.div`
  min-width: 0;
`;

const Label = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  line-height: 1.2;
`;

const Value = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${(props) => props.$color || "#262626"};
  line-height: 1.2;
`;

const CARD_DEFS = [
  {
    key: "active",
    label: "Active Loads",
    shortLabel: "Active",
    countKey: "activeLoads",
    icon: TruckOutlined,
    accent: "#52c41a",
    bg: "#f6ffed",
    iconBg: "#b7eb8f",
  },
  {
    key: "collecting_today",
    label: "Collecting Today",
    shortLabel: "Collecting",
    countKey: "collectingToday",
    icon: ClockCircleOutlined,
    accent: "#faad14",
    bg: "#fffbe6",
    iconBg: "#ffe58f",
  },
  {
    key: "in_transit",
    label: "In Transit",
    shortLabel: "In Transit",
    countKey: "inTransit",
    icon: CarOutlined,
    accent: "#1890ff",
    bg: "#e6f7ff",
    iconBg: "#91d5ff",
  },
  {
    key: "delivering_today",
    label: "Delivering Today",
    shortLabel: "Delivering",
    countKey: "deliveringToday",
    icon: SendOutlined,
    accent: "#52c41a",
    bg: "#f6ffed",
    iconBg: "#b7eb8f",
  },
  {
    key: "needs_attention",
    label: "Needs Attention",
    shortLabel: "Attention",
    countKey: "needsAttention",
    icon: WarningOutlined,
    accent: "#ff4d4f",
    bg: "#fff1f0",
    iconBg: "#ffa39e",
    emphasis: true,
  },
];

/**
 * @param {{
 *   summary: { activeLoads: number, collectingToday: number, inTransit: number, deliveringToday: number, needsAttention: number },
 *   activeKey: string,
 *   onSelect: (key: string) => void,
 * }} props
 */
export default function OperationalSummaryCards({
  summary,
  activeKey,
  onSelect,
}) {
  return (
    <>
      <CompactRow>
        {CARD_DEFS.map((def) => {
          const Icon = def.icon;
          const count = summary?.[def.countKey] ?? 0;
          const isActive = activeKey === def.key;
          return (
            <Chip
              key={`chip-${def.key}`}
              type="button"
              $active={isActive}
              $accent={def.accent}
              $bg={def.bg}
              $emphasis={def.emphasis && count > 0}
              onClick={() => onSelect?.(def.key)}
              aria-pressed={isActive}
            >
              <ChipTop $bg={def.iconBg} $color={def.accent}>
                <Icon />
              </ChipTop>
              <ChipCount
                $color={def.emphasis && count > 0 ? def.accent : undefined}
              >
                {count}
              </ChipCount>
              <ChipLabel>{def.shortLabel}</ChipLabel>
            </Chip>
          );
        })}
      </CompactRow>

      <CardsRow>
        {CARD_DEFS.map((def) => {
          const Icon = def.icon;
          const count = summary?.[def.countKey] ?? 0;
          const isActive = activeKey === def.key;
          return (
            <Card
              key={def.key}
              type="button"
              $active={isActive}
              $accent={def.accent}
              $bg={def.bg}
              $emphasis={def.emphasis && count > 0}
              onClick={() => onSelect?.(def.key)}
              aria-pressed={isActive}
            >
              <IconWrap $bg={def.iconBg} $color={def.accent}>
                <Icon />
              </IconWrap>
              <Meta>
                <Label>{def.label}</Label>
                <Value
                  $color={def.emphasis && count > 0 ? def.accent : undefined}
                >
                  {count}
                </Value>
              </Meta>
            </Card>
          );
        })}
      </CardsRow>
    </>
  );
}
